import { useEffect, useRef, type ReactNode } from 'react'

/**
 * A right-anchored panel for one focused task: granting access, appointing a
 * role, reading one person's grants.
 *
 * ---------------------------------------------------------------------------
 * WHY A PANEL AND NOT AN INLINE FORM
 *
 * The screens this replaces kept both grant forms permanently open above the
 * lists they modified. Two consequences, both bad: the forms pushed the data
 * down the page so the first thing an administrator saw on arriving was an
 * empty text box rather than the current state, and a form that is always open
 * is always half-filled — it invites a stray keystroke on a screen where the
 * submit button changes who can read a company's balances.
 *
 * A panel makes granting an ACT: opened deliberately, dismissed deliberately,
 * and while it is open it is the only thing being done.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT A CENTRED MODAL
 *
 * Because the answer to "who already has this?" is behind it. A centred dialog
 * covers the list; a side panel leaves it readable, so an administrator can
 * check whether somebody is already on the list without abandoning what they
 * have typed. On this screen that lookup is the common case, not the rare one.
 *
 * ---------------------------------------------------------------------------
 * z-40, DELIBERATELY BELOW THE LOADER
 *
 * BrandLoader is fixed to the centre of the screen at z-50 and is
 * pointer-events-none. Putting the panel above it would hide the platform's
 * only "something is happening" signal at precisely the moment a panel is
 * submitting. So the panel sits under it and the loader shows through.
 */
export function SlideOver({
  title,
  heading,
  description,
  onClose,
  children,
  footer,
  width = 'md',
}: {
  title: string
  /** What the header shows when it is more than the title; the title still names the panel to a screen reader. */
  heading?: ReactNode
  /** One sentence on what this panel does. Not decoration — it carries the rule. */
  description?: ReactNode
  onClose: () => void
  children: ReactNode
  /** Actions. Pinned to the bottom so they stay reachable in a long panel. */
  footer?: ReactNode
  width?: 'md' | 'lg'
}) {
  const panel = useRef<HTMLDivElement>(null)

  // Where focus came FROM, so it can be put back. Without this, dismissing a
  // panel drops a keyboard user at the top of the document and they have to
  // tab all the way back to the row they were working on.
  const opener = useRef<Element | null>(null)

  useEffect(() => {
    opener.current = document.activeElement

    // First field, not the panel itself: the point of opening this is to type.
    const first = panel.current?.querySelector<HTMLElement>(
      'input, textarea, select, button:not([data-panel-dismiss])',
    )
    first?.focus()

    return () => {
      (opener.current as HTMLElement | null)?.focus?.()
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }

      // A CRUDE FOCUS TRAP, and crude is the right amount here.
      //
      // Tab must not walk out of an open panel into the list behind it — a
      // keyboard user would be typing into a form they can no longer see. This
      // wraps at both ends rather than pulling in a focus-management library
      // for one panel.
      if (e.key !== 'Tab' || !panel.current) return
      const focusable = panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        aria-hidden
        onClick={onClose}
        className="anim-scrim-in absolute inset-0 bg-(--color-ink)/25 backdrop-blur-[1px]"
      />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`anim-panel-in relative flex h-full w-full flex-col border-l border-(--color-line-strong) bg-(--color-surface) shadow-(--shadow-raised) ${
          width === 'lg' ? 'max-w-2xl' : 'max-w-lg'
        }`}
      >
        <header className="flex shrink-0 items-start justify-between gap-6 border-b border-(--color-line) px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-(length:--text-lg) font-semibold tracking-[-0.01em] text-(--color-ink)">
              {heading ?? title}
            </h2>
            {description && (
              <p className="mt-1.5 text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            data-panel-dismiss
            onClick={onClose}
            aria-label="Close"
            className="-mr-1.5 -mt-1 shrink-0 rounded p-1.5 text-(--color-ink-3) transition-colors hover:bg-(--color-surface-3) hover:text-(--color-ink)"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* The only scrolling region in the panel, so the header and the
            actions stay put however long the body gets. */}
        <div className="scroll-slim min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-(--color-line) bg-(--color-surface-2) px-5 py-3.5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
